import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type ApiError,
  type ExportArtifactType,
  driveAuthorizeUrl,
  driveExport,
  driveStatus,
} from '../../lib/api';
import {
  type ClassifiedDriveError,
  type DriveButtonPhase,
  type DriveConnectAttemptState,
  classifyDriveError,
  driveButtonLabel,
  escalateIfLoopTripped,
  markDriveEverConnected,
  needsConnectAffordance,
  readDriveConnectAttemptStorage,
  recordDriveReconnectFailure,
  resetDriveReconnectFailures,
  writeDriveConnectAttemptStorage,
} from '../../lib/driveExportState';
import { writeDriveScrollMarker } from '../../lib/driveScrollRestore';
import styles from './DriveSaveButton.module.css';

// =============================================================================
// DriveSaveButton — "Save to Google Drive" v1 (docs/design/drive-export-
// 2026-07-05.md). Mounted beside the Print button on every output-document
// surface: CorpusResumePanel, JobResumePanel, and the cover-letter card in
// JobDetail.tsx. Same house shape as those Print buttons — a single action,
// honest states, Mira-voiced copy — never a silent background sync (rule #1,
// human-in-the-loop: this is a user tap, per document, every time).
//
// STATE MACHINE (driveExportState.ts owns the pure logic; this component is
// the thin JSX shell around it):
//   checking → not-connected (no live grant) OR idle (grant found)
//   idle → saving → saved (real webViewLink) | error
//   error classified 'reconnect'/'not_connected' → offers the SAME connect
//   affordance as not-connected (needsConnectAffordance)
//
// THIN PRESENTATION / NO INVENTION: this component invents nothing — it only
// renders the server's own status/result/error shapes through
// driveExportState.ts's pure classifiers. The webViewLink it opens is always
// the exact URL the server returned, never constructed client-side.
// =============================================================================

export function DriveSaveButton({
  artifactType,
  artifactId,
  templateFamily,
  accentKey,
}: {
  artifactType: ExportArtifactType;
  /** The artifact's own row id (resumes.id / coverLetters.id). Absent when
   *  the artifact hasn't been built/saved yet (e.g. a résumé panel before
   *  the first "Build résumé" click) — the button doesn't mount at all in
   *  that case (see the three mount sites: each only renders this once a
   *  real id exists), so this is typed required here, not optional. */
  artifactId: string;
  /** TEMPLATES-P2 (§6). OPTIONAL — omitted entirely for the cover-letter/
   *  essay mount sites (they have no template concept) and for any resume
   *  mount that hasn't wired a TemplatePicker yet (byte-identical to before
   *  this lane). Threaded straight through to POST /exports/drive; the
   *  server is the one and only place a premium family is actually gated
   *  (§6, never a client-only check) — this component just surfaces the
   *  honest 402 body via classifyDriveError when the server says no. */
  templateFamily?: string;
  accentKey?: string | null;
}) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<DriveButtonPhase>('checking');
  const [classifiedError, setClassifiedError] = useState<ClassifiedDriveError | null>(null);
  const [webViewLink, setWebViewLink] = useState<string | null>(null);
  // SEC-4 · A-P4 — the loop-breaker's own attempt state, seeded from
  // sessionStorage so it survives the full-page OAuth round trip
  // (handleConnect navigates via window.location.href; this component fully
  // unmounts and remounts on the way back). See driveExportState.ts's own
  // module header for why sessionStorage (tab-scoped, not permanent).
  const [attemptState, setAttemptState] = useState<DriveConnectAttemptState>(() =>
    readDriveConnectAttemptStorage(),
  );

  useEffect(() => {
    let cancelled = false;
    driveStatus()
      .then((r) => {
        if (cancelled) return;
        setPhase(r.connected ? 'idle' : 'not-connected');
        if (r.connected) {
          const next = markDriveEverConnected(readDriveConnectAttemptStorage());
          setAttemptState(next);
          writeDriveConnectAttemptStorage(next);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // A failed status check degrades to not-connected — the honest
        // "I don't know, so let's (re)connect" state, never a silent stall
        // on 'checking' forever.
        setPhase('not-connected');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleConnect() {
    try {
      const { url } = await driveAuthorizeUrl(window.location.pathname);
      // W15 (docs/WALK-FINDINGS-2026-07-21.md) — his 00:19 essay-walk finding:
      // this same-tab full-page navigate reAuths correctly but reloads the
      // app fresh on return, dumping the reader back at the TOP of the page.
      // Capture the REAL scrolling element's position (AppShell's own
      // `.scrollArea`, never window.scrollY — see driveScrollRestore.ts's own
      // header for why) right before leaving, keyed to this exact path so the
      // restore on the way back never applies it to an unrelated screen.
      // Best-effort: a missing scroll host or a sessionStorage write failure
      // just means nothing gets restored later, never a blocked navigation.
      const scrollHost = document.querySelector<HTMLElement>('[class*="scrollArea"]');
      if (scrollHost) {
        writeDriveScrollMarker(window.location.pathname, scrollHost.scrollTop, Date.now());
      }
      window.location.href = url;
    } catch {
      setPhase('error');
      setClassifiedError({
        kind: 'unknown',
        message: "I couldn't start the Google Drive connection — try again in a moment.",
      });
    }
  }

  async function handleSave() {
    setPhase('saving');
    setClassifiedError(null);
    try {
      const result = await driveExport(artifactType, artifactId, templateFamily, accentKey);
      setWebViewLink(result.webViewLink);
      setPhase('saved');
      // A real success is proof the connection works end-to-end — clears the
      // loop-breaker's failure count (SEC-4).
      const cleared = resetDriveReconnectFailures(attemptState);
      setAttemptState(cleared);
      writeDriveConnectAttemptStorage(cleared);
    } catch (err) {
      const apiErr = err as ApiError;
      const raw = classifyDriveError(apiErr?.body);
      let classified = raw;
      if (raw.kind === 'reconnect' || raw.kind === 'not_connected') {
        // SEC-4 · A-P4 — count this reconnect-shaped failure; a SECOND one
        // since the last confirmed connect (or successful save) escalates to
        // the terminal, honest 'connection_broken' state — never an
        // infinite "reconnect, fail, reconnect, fail" loop.
        const next = recordDriveReconnectFailure(attemptState);
        setAttemptState(next);
        writeDriveConnectAttemptStorage(next);
        classified = escalateIfLoopTripped(raw, next);
      }
      setClassifiedError(classified);
      setPhase('error');
    }
  }

  function handleOpen() {
    if (webViewLink) window.open(webViewLink, '_blank', 'noopener,noreferrer');
  }

  const label = driveButtonLabel(phase, classifiedError?.kind ?? null);
  const showConnect = needsConnectAffordance(phase, classifiedError?.kind ?? null);
  const needsUpgrade = phase === 'error' && classifiedError?.kind === 'entitlement_required';
  // SEC-4 — once the loop-breaker has tripped (classifiedError.kind ===
  // 'connection_broken'), the button goes fully inert: neither a retry nor
  // another reconnect can fix a server-side storage bug, so clicking must do
  // nothing rather than silently re-attempt the same futile action.
  const isConnectionBroken = classifiedError?.kind === 'connection_broken';
  const disabled = phase === 'checking' || phase === 'saving' || isConnectionBroken;

  function handleClick() {
    if (phase === 'checking' || phase === 'saving') return;
    if (phase === 'saved') return handleOpen();
    // A 402 can never be fixed by retrying the same request — send the user
    // to billing instead of re-attempting a doomed export (TEMPLATES-P2 §6).
    if (needsUpgrade) return navigate('/settings');
    if (showConnect) return handleConnect();
    return handleSave();
  }

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={phase === 'saved' ? styles.savedButton : styles.saveButton}
        onClick={handleClick}
        disabled={disabled}
      >
        {label}
      </button>
      {phase === 'error' && classifiedError ? (
        <div className={styles.errorNote} role="alert">
          {classifiedError.message}
        </div>
      ) : null}
    </div>
  );
}
